"use client";

import { Box, Button, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import Link from "next/link";
import { useState, type FormEvent, type ReactElement } from "react";

import { FormField } from "@/components/ui";
import { safeCapture } from "@/lib/analytics";
import { api } from "@/lib/api/client";
import { readCheckoutContext } from "@/lib/analytics/checkout-context";
import { rememberLeadId } from "@/lib/analytics/first-touch";

interface LeadGateFormProps {
  slug: string;
  destinationName: string;
}

type FieldErrors = { firstName?: string; email?: string };

/**
 * The free monthly summary sign-up.
 *
 * It unlocks nothing. Leaving an email used to mint a 30-day token that
 * revealed the entire report; the report is now sold at `/reports/[slug]`, and
 * what this buys is the free monthly digest.
 *
 * Inline and below the locked sections rather than a modal on the paywall
 * button: it must not compete with the purchase CTA, and a dialog that opens
 * where a reader expected the report is a bait-and-switch.
 */
export function LeadGateForm({
  slug,
  destinationName,
}: LeadGateFormProps): ReactElement {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    const nextErrors: FieldErrors = {};
    if (!firstName.trim()) {
      nextErrors.firstName = "First name is required.";
    }
    if (!email.trim()) {
      nextErrors.email = "Email is required.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const created = await api.post<{ ok: true; leadId?: string }>(
        `/api/public/destinations/${slug}/leads`,
        {
          email: email.trim(),
          firstName: firstName.trim(),
          ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
          ...(company.trim() ? { company: company.trim() } : {}),
          marketingConsent,
          ...readCheckoutContext(slug),
        },
        { requireAuth: false }
      );
      if (created.leadId) {
        rememberLeadId(slug, created.leadId);
      }

      // Never send the email address to PostHog.
      safeCapture(
        "destination_report_lead_captured",
        { slug, marketing_consent: marketingConsent },
        "destinations"
      );
      setSubmitted(true);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Could not sign you up."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      p={{ base: 5, md: 6 }}
      bg="var(--color-surface-tint)"
      border="1px solid var(--color-border)"
      borderRadius="var(--radius-md)"
    >
      {/* A real heading, not styled text: this is a distinct page section and
          a screen reader has to be able to jump to it. Reading tier (20/22)
          per design.md § Heading scale — below the two Close-tier asks it
          sits between, because a free email capture is not the commercial
          ask on this page. */}
      <Heading
        as="h2"
        fontFamily="var(--font-display)"
        fontSize={{ base: "20px", md: "22px" }}
        fontWeight="600"
        letterSpacing="-0.02em"
        color="var(--color-text-primary)"
        mb={1}
        style={{ overflowWrap: "anywhere", minWidth: 0 }}
      >
        Get next month&apos;s {destinationName} summary
      </Heading>

      {isSubmitted ? (
        <Text
          fontSize="14px"
          color="var(--color-text-secondary)"
          lineHeight="1.55"
        >
          Done — we&apos;ll email you when the {destinationName} rankings move.
        </Text>
      ) : (
        <>
          <Text
            fontSize="14px"
            color="var(--color-text-secondary)"
            lineHeight="1.55"
            mb={5}
          >
            A short note on what changed on this page, once a month.
          </Text>

          <form onSubmit={handleSubmit} noValidate>
            <Stack gap={4}>
              {formError && (
                <Box
                  p={3}
                  bg="rgba(239, 68, 68, 0.08)"
                  border="0.5px solid rgba(239, 68, 68, 0.2)"
                  borderRadius="8px"
                  role="alert"
                >
                  <Text fontSize="14px" color="rgb(220, 38, 38)">
                    {formError}
                  </Text>
                </Box>
              )}

              <FormField
                label="First name"
                value={firstName}
                onChange={setFirstName}
                placeholder="Ana"
                autoComplete="given-name"
                error={fieldErrors.firstName}
              />

              <FormField
                label="Last name (optional)"
                value={lastName}
                onChange={setLastName}
                autoComplete="family-name"
              />

              <FormField
                label="Company (optional)"
                value={company}
                onChange={setCompany}
                autoComplete="organization"
              />

              <FormField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                error={fieldErrors.email}
              />

              {/* Unticked by design — pre-ticking invalidates consent. */}
              <Flex as="label" gap={3} alignItems="flex-start" cursor="pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  style={{
                    marginTop: "3px",
                    width: "16px",
                    height: "16px",
                    accentColor: "var(--color-primary)",
                    flexShrink: 0,
                  }}
                />
                <Text
                  fontSize="13px"
                  color="var(--color-text-secondary)"
                  lineHeight="1.5"
                >
                  Also email me when the rankings in {destinationName} change
                  — about once a month.
                </Text>
              </Flex>

              {/* Secondary voice: hairline border, not a fill. This sits within
                  a screen of the report's purchase CTA, and design.md § CTA
                  voice allows one filled CTA per viewport — the paid ask owns
                  it. A free email capture must not look like the purchase. */}
              <Button
                type="submit"
                bg="transparent"
                color="var(--color-text-primary)"
                border="1px solid var(--color-border-strong)"
                h="44px"
                fontWeight="600"
                fontSize="15px"
                borderRadius="var(--radius-sm)"
                transition="var(--transition-fast)"
                _hover={{
                  borderColor: "var(--color-primary)",
                  color: "var(--color-primary)",
                }}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Email me the summary
              </Button>

              <Text fontSize="12px" color="var(--color-text-tertiary)" lineHeight="1.5">
                We never spam. By continuing you agree to our{" "}
                <Link
                  href="/legal/terms"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  Terms
                </Link>
                .
              </Text>
            </Stack>
          </form>
        </>
      )}
    </Box>
  );
}
